import { useState } from "react";
import { Info } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "../../../components/ui/radio-group";

/**
 * "Auto-RSVP" section. A 4-tier radio (off / notify / auto / auto-override)
 * mirroring AutoVoteSection, plus a Maybe-vs-No target toggle that's only
 * meaningful for the two action tiers. Governs what happens to your RSVP on a
 * finalized session when a conflict-marked calendar event overlaps it.
 *
 * Inline-expandable info per row (touch-reachable, no nested Radix popover) —
 * same affordance as AutoVoteSection, since this is a consequential setting that
 * must be readable on a phone.
 */

const TIERS = [
  {
    value: "off",
    label: "Off",
    description: "A calendar conflict never changes your RSVP.",
    longExplanation:
      "If a calendar event clashes with a session you're going to, you'll still get the usual heads-up notification — but your RSVP stays exactly as you set it. Pick this if you'd rather decide every RSVP by hand.",
  },
  {
    value: "notify",
    label: "Notify only",
    description: "Tells you about a conflict, but never changes your RSVP.",
    longExplanation:
      "When a new conflict overlaps a session you're going to, we'll send you a single heads-up so you can adjust your RSVP yourself. We never change it for you.",
  },
  {
    value: "auto",
    label: "Auto-update (keeps your changes)",
    description:
      "Downgrades your RSVP on a conflict, and restores it when the conflict clears.",
    longExplanation:
      "When a conflict overlaps a session you're going to, we'll change your RSVP for you (to Maybe or Can't-go, your choice below) and notify you. When the conflict clears, we restore your previous RSVP. If you set your RSVP back by hand, we leave it alone — we won't fight you.",
  },
  {
    value: "auto-override",
    label: "Auto-update with override",
    description:
      "Same as Auto-update, but also overrides an RSVP you set by hand.",
    longExplanation:
      "Same as Auto-update, plus: even an RSVP you placed yourself gets downgraded when a conflict appears (once — if you set it back, it stays). Pick this if you want your RSVP to always reflect your latest calendar.",
  },
];

const TARGETS = [
  { value: "maybe", label: "Maybe" },
  { value: "no", label: "Can't go" },
];

const actionTier = (mode) => mode === "auto" || mode === "auto-override";

export function AutoRsvpSettingsCard({
  autoRsvpMode = "off",
  autoRsvpConflictTarget = "maybe",
  onAutoRsvpModeChange,
  onAutoRsvpConflictTargetChange,
  saving = false,
}) {
  const [expandedTier, setExpandedTier] = useState(null);
  const targetDisabled = saving || !actionTier(autoRsvpMode);

  return (
    <section className="space-y-2 border-t border-slate-200 pt-3 dark:border-slate-700">
      <h3 id="auto-rsvp-heading" className="font-semibold">Auto-RSVP on conflicts</h3>
      <RadioGroup
        value={autoRsvpMode}
        onValueChange={onAutoRsvpModeChange}
        disabled={saving}
        aria-labelledby="auto-rsvp-heading"
        className="space-y-2"
      >
        {TIERS.map((tier) => {
          const expanded = expandedTier === tier.value;
          return (
            <div
              key={tier.value}
              className="rounded-xl border border-slate-200 bg-white/60 px-3 py-2.5 dark:border-slate-700 dark:bg-slate-900/40"
            >
              <div className="flex items-start gap-2">
                <label
                  htmlFor={`auto-rsvp-${tier.value}`}
                  className="flex flex-1 cursor-pointer items-start gap-3"
                >
                  <RadioGroupItem
                    id={`auto-rsvp-${tier.value}`}
                    value={tier.value}
                    className="mt-1 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    aria-label={tier.label}
                  />
                  <span className="flex flex-col gap-1">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      {tier.label}
                    </span>
                    <span className="text-[11px] text-slate-500 dark:text-slate-400">
                      {tier.description}
                    </span>
                  </span>
                </label>
                <button
                  type="button"
                  aria-label={`More about ${tier.label}`}
                  aria-expanded={expanded}
                  onClick={() => setExpandedTier(expanded ? null : tier.value)}
                  className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-full text-slate-400 motion-safe:transition-colors hover:bg-slate-100 hover:text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:text-slate-500 dark:hover:bg-slate-800 dark:hover:text-slate-300"
                >
                  <Info className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
              {expanded && (
                <p className="mt-2 border-t border-slate-200 pt-2 text-xs leading-relaxed text-slate-600 dark:border-slate-700 dark:text-slate-300">
                  {tier.longExplanation}
                </p>
              )}
            </div>
          );
        })}
      </RadioGroup>

      <div className="rounded-xl border border-slate-200 bg-white/60 px-3 py-2.5 dark:border-slate-700 dark:bg-slate-900/40">
        <p id="auto-rsvp-target-label" className="text-[11px] font-medium text-slate-600 dark:text-slate-300">
          When a conflict downgrades you, set RSVP to
          {!actionTier(autoRsvpMode) ? (
            <span id="auto-rsvp-target-hint" className="font-normal text-slate-400 dark:text-slate-500">
              {" (only applies to the Auto-update tiers)"}
            </span>
          ) : null}
        </p>
        <RadioGroup
          value={autoRsvpConflictTarget}
          onValueChange={onAutoRsvpConflictTargetChange}
          disabled={targetDisabled}
          aria-labelledby={`auto-rsvp-target-label${
            !actionTier(autoRsvpMode) ? " auto-rsvp-target-hint" : ""
          }`}
          className="mt-2 flex gap-4"
        >
          {TARGETS.map((t) => (
            <label
              key={t.value}
              htmlFor={`auto-rsvp-target-${t.value}`}
              className={`flex min-h-6 items-center gap-2 py-1 text-sm ${
                targetDisabled
                  ? "cursor-not-allowed text-slate-400 dark:text-slate-500"
                  : "cursor-pointer text-slate-700 dark:text-slate-200"
              }`}
            >
              <RadioGroupItem
                id={`auto-rsvp-target-${t.value}`}
                value={t.value}
                aria-label={t.label}
                disabled={targetDisabled}
                className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
              {t.label}
            </label>
          ))}
        </RadioGroup>
      </div>
    </section>
  );
}
